import { useMutation } from '@tanstack/react-query';
import { useCallback, useRef } from 'react';
import type { DocumentNode } from 'graphql';
import { checkHasValidValues } from '@app/utils';
import fetchEdge from '../../utils/fetchEdge';

interface HeaderOptions {
  context?: {
    headers?: { [x: string]: string };
  };
}

interface HeaderMutateOptions {
  context?: {
    headers?: { [x: string]: Nullable<string> | undefined };
  };
}

/**
 *
 * @param query
 * @param options
 */
const useEdgeMutation = <
  TData extends Dict = Dict,
  TVariables extends Dict = Dict,
>(
  query: DocumentNode,
  options: Dict = {},
) => {
  const contextRef = useRef<HeaderOptions>(options?.context);
  const { context, ...restOfOptions } = options;
  const queryFn = useCallback(
    (variables: TVariables) =>
      fetchEdge<TData>({ queryKey: [{}, { variables }] }, query, {
        context: contextRef.current?.context,
      }),
    [query],
  );

  const { status, mutateAsync, ...queryObject } = useMutation<
    TData,
    Error,
    TVariables
  >(queryFn, {
    ...restOfOptions,
  } as Dict<any>);

  const mutatefn = (
    mutationVariables: TVariables,
    options: HeaderMutateOptions = {},
  ) => {
    if (
      options?.context?.headers &&
      checkHasValidValues({ toCheck: options.context.headers }) // this checks undefined values
    ) {
      contextRef.current = options as HeaderOptions; //because of the above check - using type assertion here
    }

    return mutateAsync(mutationVariables);
  };

  return {
    loading: status === 'loading',
    status,
    mutatefn,
    ...queryObject,
  };
};

export default useEdgeMutation;
